import { useState } from 'react';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useUserContext } from '@hooks/context/useUserContext';
import { apiOrderService } from '@api';
import { EUserType } from '@xeppt/xeppt-sdk/types';

/**
 * Single source of truth for iGaming visibility.
 *
 * An individual (non-business) account may see the iGaming section only if it is
 * actually receiving payouts from iGaming businesses; business accounts never
 * see it. Used by BOTH the header menu link and the `/igaming` route guard so
 * the two rules can never drift apart (a hidden menu entry must also mean an
 * unreachable route).
 */
export const useIgamingAccess = () => {
    const { user } = useUserContext();
    const isIndividual = user?.type === EUserType.INDIVIDUAL;

    // Track query settlement with our own state. We deliberately do NOT rely on
    // useApiQuery's internal isLoading here: it is seeded from `isInitialLoading`
    // via useState and frozen at first mount. On a fresh /igaming load the user
    // context is still undefined (so isIndividual is false at that first mount),
    // which means a mount-frozen loading flag would never reflect the query that
    // fires once the user resolves to an individual — bouncing a legitimate
    // eligible user to the dashboard before their eligibility query even runs.
    // `onFinish` fires on both success and error, so `settled` flips exactly when
    // we can decide.
    const [settled, setSettled] = useState(false);

    const { data } = useApiQuery({
        method: () =>
            apiOrderService.findIndividualSales({ page_size: 1, page: 1, order: 'DESC' }),
        deps: [user],
        condition: isIndividual,
        isInitialRequest: true,
        onFinish: () => setSettled(true)
    });

    const hasIgamingPayouts = (data?.total ?? 0) > 0;

    return {
        // Mirrors the header menu rule exactly.
        hasAccess: !!isIndividual && hasIgamingPayouts,
        // True until we can decide: the user hasn't loaded yet, or (for an
        // individual) the sales query hasn't settled. Callers must not redirect
        // while this is true.
        isResolving: !user || (isIndividual && !settled)
    };
};
